// One lesson's body: its videos (sortable with dnd-kit, add / edit in a modal
// with a Markdown transcript) and its knowledge check. Every change saves
// straight away and recomputes the lesson and course durations.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconExternalLink, IconGripVertical, IconPencil, IconPlayerPlay, IconPlus, IconTrash } from "@tabler/icons-react";
import type { KnowledgeCheck, Lesson, Video } from "../../types/course";
import { errorText } from "../../services/courses/auth";
import { updateLesson } from "../../services/courses/lesson.service";
import {
  createVideo,
  deleteVideo,
  extractYouTubeId,
  getVideosByLessonId,
  getYouTubeThumbnail,
  reorderVideos,
  updateVideo,
} from "../../services/courses/video.service";
import { KnowledgeCheckManager } from "./KnowledgeCheckManager";
import { MarkdownEditor } from "./MarkdownEditor";
import { MarkdownPreview } from "./MarkdownPreview";
import { Modal } from "../Modal";

interface VideoDraft {
  title: string;
  youtubeUrl: string;
  duration: number;
  transcript: string;
}

const emptyDraft = (): VideoDraft => ({ title: "", youtubeUrl: "", duration: 5, transcript: "" });

function Thumb({ video, large }: { video: Pick<Video, "youtubeUrl" | "thumbnail" | "duration" | "title">; large?: boolean }) {
  const src = video.thumbnail || getYouTubeThumbnail(video.youtubeUrl);
  return (
    <div className={`co-thumb${large ? " lg" : ""}`}>
      {src ? <img src={src} alt="" /> : <IconPlayerPlay size={20} />}
      <span className="dur">{video.duration} min</span>
    </div>
  );
}

function SortableVideo({
  video,
  disabled,
  onEdit,
  onDelete,
}: {
  video: Video;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: video.id!,
    disabled,
  });
  const [showTranscript, setShowTranscript] = useState(false);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="co-video">
      <button type="button" className="co-grip" title="Drag to reorder" aria-label={`Reorder ${video.title}`}
        disabled={disabled} {...attributes} {...listeners}>
        <IconGripVertical size={14} />
      </button>
      <Thumb video={video} />
      <div className="co-video-info">
        <div className="t">{video.title}</div>
        <div className="d">
          <span>{video.duration} min</span>
          <a href={video.youtubeUrl} target="_blank" rel="noreferrer">
            Watch on YouTube <IconExternalLink size={11} />
          </a>
          {video.transcript ? (
            <button type="button" className="tt-link" onClick={() => setShowTranscript((v) => !v)}>
              {showTranscript ? "Hide transcript" : "Transcript"}
            </button>
          ) : (
            <span>No transcript</span>
          )}
        </div>
      </div>
      <div className="co-actions">
        <button type="button" className="co-btn-icon" onClick={onEdit} disabled={disabled} title="Edit video" aria-label="Edit video">
          <IconPencil size={14} />
        </button>
        <button type="button" className="co-btn-icon danger" onClick={onDelete} disabled={disabled} title="Delete video" aria-label="Delete video">
          <IconTrash size={14} />
        </button>
      </div>
      {showTranscript && video.transcript && (
        <div className="co-transcript">
          <MarkdownPreview content={video.transcript} compact />
        </div>
      )}
    </div>
  );
}

export function VideoManager({
  courseId,
  lesson,
  onChanged,
  disabled,
}: {
  courseId: string;
  lesson: Lesson;
  // Called after any write so the page can refresh lesson and course totals.
  onChanged: () => void;
  disabled?: boolean;
}) {
  const lessonId = lesson.id!;
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Video | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<VideoDraft>(emptyDraft());
  const [draftError, setDraftError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setVideos(await getVideosByLessonId(courseId, lessonId));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [courseId, lessonId]);

  useEffect(() => {
    load();
  }, [load]);

  function openModal(video: Video | null) {
    setEditing(video);
    setDraft(
      video
        ? { title: video.title, youtubeUrl: video.youtubeUrl, duration: video.duration, transcript: video.transcript ?? "" }
        : emptyDraft(),
    );
    setDraftError(null);
    setModalOpen(true);
  }

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
    setDraftError(null);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const title = draft.title.trim();
    const youtubeUrl = draft.youtubeUrl.trim();
    if (!title) return setDraftError("Please enter a video title.");
    if (!extractYouTubeId(youtubeUrl)) return setDraftError("That does not look like a YouTube URL.");
    if (!(draft.duration > 0)) return setDraftError("Enter the video length in minutes.");

    const data = {
      title,
      youtubeUrl,
      duration: draft.duration,
      thumbnail: getYouTubeThumbnail(youtubeUrl),
      transcript: draft.transcript,
    };
    setBusy(true);
    setDraftError(null);
    try {
      if (editing) await updateVideo(courseId, lessonId, editing.id!, data);
      else {
        // Deletes do not renumber, so the list length could collide with an existing order.
        const order = videos.reduce((max, v) => Math.max(max, v.order + 1), 0);
        await createVideo(courseId, lessonId, { ...data, order });
      }
      closeModal();
      await load();
      onChanged();
    } catch (err) {
      setDraftError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(video: Video) {
    if (!confirm(`Delete “${video.title}”?`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteVideo(courseId, lessonId, video.id!);
      await load();
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = videos.findIndex((v) => v.id === active.id);
    const newIndex = videos.findIndex((v) => v.id === over.id);
    const reordered = arrayMove(videos, oldIndex, newIndex);
    setVideos(reordered); // optimistic; the order field follows
    try {
      await reorderVideos(courseId, lessonId, reordered.map((v) => v.id!));
    } catch (e) {
      setError(`Reorder failed: ${errorText(e)}`);
      await load();
    }
  }

  async function updateKnowledgeCheck(knowledgeCheck?: KnowledgeCheck) {
    await updateLesson(courseId, lessonId, { knowledgeCheck });
    onChanged();
  }

  const locked = !!disabled || busy;
  const ytId = extractYouTubeId(draft.youtubeUrl.trim());

  return (
    <div className="co-lesson-body">
      <div className="co-sub-head">
        Videos <span className="count">{videos.length}</span>
        <div className="spacer" />
        <button type="button" className="btn btn-sm" onClick={() => openModal(null)} disabled={locked}>
          <IconPlus size={14} /> Add video
        </button>
      </div>
      {error && <div className="error-text">{error}</div>}
      {loading ? (
        <div className="co-empty-inline">Loading videos…</div>
      ) : videos.length === 0 ? (
        <div className="co-empty-inline">No videos in this lesson yet.</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={videos.map((v) => v.id!)} strategy={verticalListSortingStrategy}>
            <div className="co-video-list">
              {videos.map((v) => (
                <SortableVideo key={v.id} video={v} disabled={locked} onEdit={() => openModal(v)} onDelete={() => remove(v)} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="co-sub-head">
        Knowledge check
        {lesson.knowledgeCheck && <span className="count">{lesson.knowledgeCheck.duration} min</span>}
      </div>
      <KnowledgeCheckManager knowledgeCheck={lesson.knowledgeCheck} onUpdate={updateKnowledgeCheck} disabled={disabled} />

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? "Edit video" : "Add video"}
        wide
        footer={
          <>
            <button type="submit" form="co-video-form" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : editing ? "Update video" : "Add video"}
            </button>
            <button type="button" className="btn" onClick={closeModal} disabled={busy}>
              Cancel
            </button>
            {draftError && <span className="error-text">{draftError}</span>}
          </>
        }
      >
        <form id="co-video-form" onSubmit={submit}>
          <div className="row">
            <div>
              <label htmlFor="co-video-title">Video title</label>
              <input id="co-video-title" type="text" value={draft.title} placeholder="Your mental health matters"
                onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              <label htmlFor="co-video-url">YouTube URL</label>
              <input id="co-video-url" type="text" inputMode="url" value={draft.youtubeUrl}
                placeholder="https://www.youtube.com/watch?v=…"
                onChange={(e) => setDraft({ ...draft, youtubeUrl: e.target.value })} />
              <div className="co-hint">
                {ytId ? (
                  <>Thumbnail filled from the URL: <span className="mono">img.youtube.com/vi/{ytId}/mqdefault.jpg</span></>
                ) : (
                  "Paste the full YouTube video URL. The thumbnail is filled from it."
                )}
              </div>
              <label htmlFor="co-video-duration">Duration (minutes)</label>
              <div className="co-dur-in">
                <input id="co-video-duration" type="number" min={1} value={draft.duration}
                  onChange={(e) => setDraft({ ...draft, duration: parseInt(e.target.value, 10) || 0 })} />
                <span>stored as a whole number of minutes, as today</span>
              </div>
            </div>
            <div>
              <label>Thumbnail preview</label>
              <Thumb video={{ youtubeUrl: draft.youtubeUrl.trim(), duration: draft.duration, title: draft.title }} large />
            </div>
          </div>
          <label>
            Transcript <span className="muted">(optional, Markdown)</span>
          </label>
          <MarkdownEditor value={draft.transcript} onChange={(transcript) => setDraft({ ...draft, transcript })} />
        </form>
      </Modal>
    </div>
  );
}
